import React, { useState, useEffect } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import { useQuery } from "@tanstack/react-query";
import AccountService from "../../../app/data/account/accountService";
import { AddSecondaryDistributorModalProps } from "./models";
import "./addSecondaryDistributorModal.scss";

const accountService = AccountService.getInstance();

const AddSecondaryDistributorModal: React.FC<AddSecondaryDistributorModalProps> = (props) => {
  const [accountSearch, setAccountSearch] = useState("");
  const [debouncedAccountSearch, setDebouncedAccountSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<XGSSelectOption | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAccountSearch(accountSearch);
    }, 300);
    return () => clearTimeout(handler);
  }, [accountSearch]);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["SEARCH_ACCOUNTS", debouncedAccountSearch],
    queryFn: async () => {
      const response = await accountService.searchAccountsByNameOrNumber(debouncedAccountSearch);
      return response?.data?.filter(account => !!account.accountName).filter(
        account => !props.existingDistributorIds.includes(account.accountId.toString())
      ) || [];
    },
  });

  const accountOptions = (accounts || []).map(account => {
    const addressParts = [
      account.address,
      account.city,
      account.state && account.zip ? `${account.state} ${account.zip}` : account.state || account.zip
    ].filter(Boolean);

    const addressLine = addressParts.length > 0 ? addressParts.join(", ") : "Address not available";

    return {
      label: `${account.accountName || 'Unnamed Account'} (${account.accountId})`,
      value: account.accountId.toString(),
      subtitle: addressLine,
    };
  });

  const handleAdd = () => {
    if (!selectedAccount || !accounts) return;

    const account = accounts.find(acc => acc.accountId.toString() === selectedAccount.value);
    if (!account) return;

    const addressParts = [
      account.address,
      account.city,
      account.state && account.zip ? `${account.state} ${account.zip}` : account.state || account.zip
    ].filter(Boolean);

    props.onAdd({
      id: account.accountId.toString(),
      accountNumber: account.accountId.toString(),
      name: account.accountName || "Unnamed Account",
      attention: account.accountAttentionName || "-",
      address: addressParts.join(", ") || "-",
    });

    setAccountSearch("");
    setSelectedAccount(null);
    props.onClose();
  };

  const handleClose = () => {
    setAccountSearch("");
    setSelectedAccount(null);
    props.onClose();
  };

  return (
    <SlideOutSidebar
      header="Add Secondary Distributor"
      show={props.show}
      onClose={handleClose}
      size="small-500"
    >
      <div className="xgs-add-secondary-distributor-modal">
        <LabeledSelectInput
          label="Search Account"
          labelMode={LabelModes.column}
          placeholder="Search by name or account number..."
          required
          isClearable
          isLoading={isLoading}
          onInputChange={(v) => setAccountSearch(v)}
          options={accountOptions}
          value={selectedAccount}
          onValueChange={(option) => setSelectedAccount(option || null)}
        />

        <div className="xgs-add-secondary-distributor-modal__buttons">
          <Button theme={ButtonThemes.gray} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            theme={ButtonThemes.blue}
            onClick={handleAdd}
            disabled={!selectedAccount}
          >
            Add
          </Button>
        </div>
      </div>
    </SlideOutSidebar>
  );
};

export default AddSecondaryDistributorModal;

